import { useQueryClient } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { useState } from 'react';

import {
  getParceirosControllerObterQueryKey,
  useParceirosControllerAvaliar,
} from '@/api/generated/parceiros/parceiros';
import { cn } from '@/shared/lib/cn';
import { toast } from '@/shared/ui/sonner';

const NOTAS = [1, 2, 3, 4, 5] as const;

/**
 * Cinco estrelas clicáveis. Uma avaliação por médico por material, editável.
 *
 * ⚠️ Não existe nota 0 — o CHECK do banco exige 1..5. "Ainda não avaliei" é
 * `minhaNota === null`, e é por isso que não há botão de limpar: mandar zero
 * seria inválido, e remover a avaliação é uma operação que ninguém pediu.
 */
export function AvaliarMaterial({
  materialId,
  parceiroId,
  minhaNota,
}: {
  materialId: string;
  parceiroId: string;
  minhaNota: number | null;
}) {
  const queryClient = useQueryClient();
  const avaliar = useParceirosControllerAvaliar();
  const [emDestaque, setEmDestaque] = useState<number | null>(null);

  // Enquanto o servidor não confirma, a estrela clicada já fica marcada: sem
  // isto o clique parece não ter funcionado até o refetch voltar.
  const notaVisivel = emDestaque ?? avaliar.variables?.data.nota ?? minhaNota ?? 0;

  function enviar(nota: number) {
    avaliar.mutate(
      { materialId, data: { nota } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: getParceirosControllerObterQueryKey(parceiroId),
          });
          toast.success('Avaliação registrada');
        },
        onError: () => toast.error('Não foi possível registrar a avaliação'),
      },
    );
  }

  return (
    <div className="inline-flex items-center gap-2" onMouseLeave={() => setEmDestaque(null)}>
      <span className="text-xs text-muted-foreground">
        {minhaNota === null ? 'Avalie:' : 'Sua nota:'}
      </span>

      <div className="inline-flex">
        {NOTAS.map((nota) => (
          <button
            key={nota}
            type="button"
            disabled={avaliar.isPending}
            onMouseEnter={() => setEmDestaque(nota)}
            onFocus={() => setEmDestaque(nota)}
            onClick={() => enviar(nota)}
            aria-label={`Dar nota ${nota} de 5`}
            aria-pressed={minhaNota === nota}
            className="p-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Star
              className={cn(
                'w-4 h-4 transition-colors',
                nota <= notaVisivel
                  ? 'text-amber-500 fill-amber-500'
                  : 'text-muted-foreground/40',
              )}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
